/**
 * SearchResultRow Component
 * Displays a food item with editable quantity, macros, and action buttons
 */

import React, { useState, useCallback, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import {
    OutgoingSearchResult,
    PassioFoodDetailResponse,
    MeasuredPortion,
} from "../types";
import {
    formatNutritionValue,
    getPortionInfo,
    calculateNutritionForPortionDirect,
    getEffectiveQuantity,
    getTotalWeightInGrams,
} from "../utils/nutritionCalculations";
import { IngredientSearchReplace } from "./IngredientSearchReplace";
import { FoodDetailModal } from "./FoodDetailModal";
import { usePassioApi } from "../hooks/usePassioApi";
import { NumberInput } from "./NumberInput";

interface SearchResultRowProps {
    searchResult: OutgoingSearchResult;
    onQuantityChange?: (newQuantity: number) => void;
    onDelete?: () => void;
    onReplace?: (newResult: OutgoingSearchResult) => void;
    showDetailedNutrients?: boolean;
}

// Export the ref type for external use
export interface SearchResultRowRef {
    getResultWithStateQuantity: () => OutgoingSearchResult;
}

// Helper function to round quantities to 2 decimal places max
const roundQuantity = (value: number): number => {
    return Math.round(value * 100) / 100;
};

export const SearchResultRow = forwardRef<SearchResultRowRef, SearchResultRowProps>(({
    searchResult,
    onQuantityChange,
    onDelete,
    onReplace,
    showDetailedNutrients = false,
}, ref) => {    
    // Initialize quantity from the search result's portion data
    const [quantity, setQuantity] = useState(() => {
        if (searchResult.nutritionPreview?.portion) {
            return roundQuantity(getEffectiveQuantity(searchResult.nutritionPreview.portion));
        }
        return 1;
    });

    // Store the nutrition per gram for consistent calculations
    const nutritionPerGram = useRef<{
        calories: number;
        protein: number;
        carbs: number;
        fat: number;
        fiber: number;
    } | null>(null);

    // Store the initial search result ID/refCode to detect when we get a truly new ingredient
    const initialIngredientId = useRef<string>("");

    // Calculate nutrition per gram from the initial search result
    useEffect(() => {
        // Only calculate if this is a new ingredient (not just a quantity update)
        const currentIngredientId = `${searchResult.resultId}-${searchResult.refCode}`;
        
        if (initialIngredientId.current !== currentIngredientId) {
            initialIngredientId.current = currentIngredientId;
            
            if (searchResult.nutritionPreview && searchResult.nutritionPreview.portion) {
                const portion = searchResult.nutritionPreview.portion;
                const effectiveQty = getEffectiveQuantity(portion);
                const totalGrams = effectiveQty * portion.weight.value;
                
                // Calculate nutrition per gram from the initial values
                nutritionPerGram.current = {
                    calories: (searchResult.nutritionPreview.calories || 0) / totalGrams,
                    protein: (searchResult.nutritionPreview.protein || 0) / totalGrams,
                    carbs: (searchResult.nutritionPreview.carbs || 0) / totalGrams,
                    fat: (searchResult.nutritionPreview.fat || 0) / totalGrams,
                    fiber: (searchResult.nutritionPreview.fiber || 0) / totalGrams,
                };
            }
        }
    }, [searchResult.resultId, searchResult.refCode, searchResult.nutritionPreview]);

    const [showSearchReplace, setShowSearchReplace] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showPortionDropdown, setShowPortionDropdown] = useState(false);
    const [availablePortions, setAvailablePortions] = useState<
        MeasuredPortion[]
    >([]);
    const [selectedPortion, setSelectedPortion] =
        useState<MeasuredPortion | null>(() => {
            // Initialize with the current portion from nutritionPreview if available
            if (searchResult.nutritionPreview?.portion) {
                return searchResult.nutritionPreview.portion;
            }
            return null;
        });
    const [loadingPortions, setLoadingPortions] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [detailedFoodData, setDetailedFoodData] =
        useState<PassioFoodDetailResponse | null>(null);
    const [detailFoodData, setDetailFoodData] =
        useState<PassioFoodDetailResponse | null>(null);

    const { client } = usePassioApi();

    const dropdownRef = useRef<HTMLDivElement>(null);

    // Reset component state when searchResult changes (ingredient replacement)
    useEffect(() => {
        // Check if this is actually a new ingredient (not just a quantity update)
        const currentIngredientId = `${searchResult.resultId}-${searchResult.refCode}`;
        
        if (initialIngredientId.current !== "" && initialIngredientId.current !== currentIngredientId) {
            // This is a true ingredient replacement, reset everything
            const newQuantity = searchResult.nutritionPreview?.portion 
                ? roundQuantity(getEffectiveQuantity(searchResult.nutritionPreview.portion))
                : 1;
            setQuantity(newQuantity);
            setShowPortionDropdown(false);
            setShowDetailModal(false);
            setAvailablePortions([]);
            setLoadingPortions(false);
            setLoadingDetails(false);
            setDetailedFoodData(null);
            setDetailFoodData(null);

            // Reset selected portion based on the new search result
            if (searchResult.nutritionPreview?.portion) {
                setSelectedPortion(searchResult.nutritionPreview.portion);
            } else {
                setSelectedPortion(null);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchResult.resultId, searchResult.refCode]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setShowPortionDropdown(false);
            }
        };

        if (showPortionDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showPortionDropdown]);

    // Calculate current macros - use detailed calculation if we have portion data
    const currentResult = React.useMemo(() => {
        if (detailedFoodData && selectedPortion) {
            // Use proper portion-based calculation
            const newNutrition = calculateNutritionForPortionDirect(
                detailedFoodData,
                selectedPortion,
                quantity
            );

            return {
                ...searchResult,
                nutritionPreview: {
                    portion: selectedPortion,
                    calories: newNutrition.calories,
                    carbs: newNutrition.carbs,
                    fat: newNutrition.fat,
                    protein: newNutrition.protein,
                    fiber: newNutrition.fiber,
                },
            };
        }

        // For basic search results without detailed data
        if (selectedPortion && nutritionPerGram.current) {
            // Calculate total grams for current portion and quantity
            const totalGrams = quantity * selectedPortion.weight.value;
            
            return {
                ...searchResult,
                nutritionPreview: {
                    portion: selectedPortion,
                    calories: Math.round(nutritionPerGram.current.calories * totalGrams),
                    carbs: Math.round(nutritionPerGram.current.carbs * totalGrams * 10) / 10,
                    fat: Math.round(nutritionPerGram.current.fat * totalGrams * 10) / 10,
                    protein: Math.round(nutritionPerGram.current.protein * totalGrams * 10) / 10,
                    fiber: Math.round(nutritionPerGram.current.fiber * totalGrams * 10) / 10,
                },
            };
        }

        // Fallback - just return the search result as is
        return searchResult;
    }, [searchResult, quantity, detailedFoodData, selectedPortion]);

    // Function to get a copy of currentResult with the state quantity applied to the portion
    const getResultWithStateQuantity = useCallback(() => {
        const resultCopy = { ...currentResult };
        
        if (resultCopy.nutritionPreview?.portion) {
            // Create a deep copy of the portion
            const portionCopy = {
                ...resultCopy.nutritionPreview.portion,
                weight: { ...resultCopy.nutritionPreview.portion.weight }
            };
            
            // Apply the state quantity to either suggestedQuantity or quantity
            if (portionCopy.suggestedQuantity && portionCopy.suggestedQuantity.length > 0) {
                portionCopy.suggestedQuantity = [quantity];
            } else {
                portionCopy.quantity = quantity;
            }
            
            // Update the result with the new portion
            resultCopy.nutritionPreview = {
                ...resultCopy.nutritionPreview,
                portion: portionCopy
            };
        }
        
        return resultCopy as OutgoingSearchResult;
    }, [currentResult, quantity]);

    const portionInfo = getPortionInfo(searchResult);

    const handleQuantityChange = useCallback(
        (newQuantity: number) => {
            if (newQuantity < 0) return;

            const roundedQuantity = roundQuantity(newQuantity);
            setQuantity(roundedQuantity);

            // Always update the ingredient data when quantity changes
            if (onReplace) {
                let updatedResult: OutgoingSearchResult;

                if (detailedFoodData && selectedPortion) {
                    // Use detailed portion-based calculation
                    const newNutrition = calculateNutritionForPortionDirect(
                        detailedFoodData,
                        selectedPortion,
                        roundedQuantity
                    );

                    updatedResult = {
                        ...searchResult,
                        nutritionPreview: {
                            portion: selectedPortion,
                            calories: newNutrition.calories,
                            carbs: newNutrition.carbs,
                            fat: newNutrition.fat,
                            protein: newNutrition.protein,
                            fiber: newNutrition.fiber,
                        },
                    };
                } else if (selectedPortion && nutritionPerGram.current) {
                    // Calculate based on nutrition per gram
                    const totalGrams = roundedQuantity * selectedPortion.weight.value;
                    
                    updatedResult = {
                        ...searchResult,
                        nutritionPreview: {
                            portion: selectedPortion,
                            calories: Math.round(nutritionPerGram.current.calories * totalGrams),
                            carbs: Math.round(nutritionPerGram.current.carbs * totalGrams * 10) / 10,
                            fat: Math.round(nutritionPerGram.current.fat * totalGrams * 10) / 10,
                            protein: Math.round(nutritionPerGram.current.protein * totalGrams * 10) / 10,
                            fiber: Math.round(nutritionPerGram.current.fiber * totalGrams * 10) / 10,
                        },
                    };
                } else {
                    // Fallback - just return current search result
                    updatedResult = searchResult;
                }

                // Always notify parent component of the updated result
                onReplace(updatedResult);
            }

            onQuantityChange?.(roundedQuantity);
        },
        [
            onQuantityChange,
            detailedFoodData,
            selectedPortion,
            searchResult,
            onReplace,
        ]
    );

    const handleReplace = useCallback(
        (newResult: OutgoingSearchResult) => {
            setShowSearchReplace(false);
            onReplace?.(newResult);
        },
        [onReplace]
    );

    const handlePortionClick = useCallback(async () => {
        if (!searchResult.refCode || !client) {
            return;
        }

        if (showPortionDropdown) {
            setShowPortionDropdown(false);
            return;
        }

        setLoadingPortions(true);
        try {
            // Use the API client's built-in caching instead of component-level cache
            const response: PassioFoodDetailResponse = await client.getFoodByRefCode(
                searchResult.refCode
            );

            // Store the detailed food data for nutrition calculations
            setDetailedFoodData(response);

            // Get portions from the first ingredient (where the actual portions are)
            const portions =
                response.results?.[0]?.ingredients?.[0]?.portions || [];
            if (portions.length > 0) {
                setAvailablePortions(portions);
                setShowPortionDropdown(true);
            }
        } catch (error) {
            console.error("Failed to fetch food details:", error);
        } finally {
            setLoadingPortions(false);
        }
    }, [searchResult.refCode, client, showPortionDropdown]);

    const handlePortionSelect = useCallback(
        (portion: MeasuredPortion) => {
            setSelectedPortion(portion);
            setShowPortionDropdown(false);

            // When changing portions, reset quantity to 1
            const newQuantity = getEffectiveQuantity(portion);
            setQuantity(newQuantity);

            // Recalculate nutrition for the new portion
            if (onReplace) {
                let updatedResult: OutgoingSearchResult;

                if (detailedFoodData) {
                    // Use detailed portion-based calculation
                    const newNutrition = calculateNutritionForPortionDirect(
                        detailedFoodData,
                        portion,
                        newQuantity
                    );

                    updatedResult = {
                        ...searchResult,
                        nutritionPreview: {
                            portion: portion,
                            calories: newNutrition.calories,
                            carbs: newNutrition.carbs,
                            fat: newNutrition.fat,
                            protein: newNutrition.protein,
                            fiber: newNutrition.fiber,
                        },
                    };
                } else if (nutritionPerGram.current) {
                    // Calculate based on nutrition per gram
                    const totalGrams = newQuantity * portion.weight.value;
                    
                    updatedResult = {
                        ...searchResult,
                        nutritionPreview: {
                            portion: portion,
                            calories: Math.round(nutritionPerGram.current.calories * totalGrams),
                            carbs: Math.round(nutritionPerGram.current.carbs * totalGrams * 10) / 10,
                            fat: Math.round(nutritionPerGram.current.fat * totalGrams * 10) / 10,
                            protein: Math.round(nutritionPerGram.current.protein * totalGrams * 10) / 10,
                            fiber: Math.round(nutritionPerGram.current.fiber * totalGrams * 10) / 10,
                        },
                    };
                } else {
                    // Fallback
                    updatedResult = searchResult;
                }

                // Notify parent component of the updated result
                onReplace(updatedResult);
            }

            // Notify parent of quantity change
            onQuantityChange?.(newQuantity);
        },
        [detailedFoodData, searchResult, onReplace, onQuantityChange]
    );

    const transformFoodDetailData = useCallback(
        (response: PassioFoodDetailResponse) => {
            // Transform PassioFoodDetailResponse to format expected by FoodDetailCard
            const result = response.results?.[0];
            const ingredient = result?.ingredients?.[0];

            if (!result || !ingredient) {
                return null;
            }

            return {
                displayName: result.displayName,
                name: result.displayName,
                type: result.type,
                iconId: result.iconId,
                internalId: result.internalId,
                refCode: result.refCode,
                nutrients: ingredient.nutrients || [],
                portions: ingredient.portions || result.portions || [],
                branded: ingredient.branded,
                origin: ingredient.origin || [],
                qualityScore: ingredient.qualityScore,
                timestamp: ingredient.timestamp,
                tags: ingredient.tags || [],
                concerns: ingredient.concerns || [],
                ingredients: [ingredient], // Wrap in array for compatibility
            };
        },
        []
    );

    const handleDetailClick = useCallback(async () => {
        if (!searchResult.refCode || !client) {
            return;
        }

        // Immediately show the modal with loading state
        setShowDetailModal(true);
        setLoadingDetails(true);
        setDetailFoodData(null); // Clear any previous data

        try {
            // Use the API client's built-in caching instead of component-level cache
            const response: PassioFoodDetailResponse = await client.getFoodByRefCode(
                searchResult.refCode
            );

            // Store the detailed food data for the modal
            setDetailFoodData(response);
        } catch (error) {
            console.error("Failed to fetch food details:", error);
            // On error, close the modal
            setShowDetailModal(false);
        } finally {
            setLoadingDetails(false);
        }
    }, [searchResult.refCode, client]);



    const nameContainerStyle: React.CSSProperties = {
        minWidth: "0", // Allow text truncation
    };

    const nameStyle: React.CSSProperties = {
        fontWeight: "600",
        fontSize: "14px",
        color: "#1a202c",
        marginBottom: "2px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    };

    const brandStyle: React.CSSProperties = {
        fontSize: "12px",
        color: "#718096",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    };

    const macrosContainerStyle: React.CSSProperties = {
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: "12px",
        fontSize: "12px",
    };

    const macroItemStyle: React.CSSProperties = {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: "0",
    };

    const macroLabelStyle: React.CSSProperties = {
        color: "#718096",
        fontSize: "10px",
        textTransform: "uppercase",
        fontWeight: "500",
    };

    const macroValueStyle: React.CSSProperties = {
        fontWeight: "600",
        color: "#2d3748",
    };

    const quantityContainerStyle: React.CSSProperties = {
        display: "grid",
        gridTemplateColumns: "80px 120px auto",
        alignItems: "center",
        gap: "6px",
        position: "relative",
    };

    const unitStyle: React.CSSProperties = {
        fontSize: "12px",
        color: searchResult.refCode ? "#3182ce" : "#718096",
        cursor: searchResult.refCode ? "pointer" : "default",
        userSelect: "none",
        padding: "4px 8px",
        border: searchResult.refCode ? "1px solid #cbd5e0" : "none",
        borderRadius: "4px",
        backgroundColor: searchResult.refCode ? "#f7fafc" : "transparent",
        display: "flex",
        alignItems: "center",
        gap: "4px",
        justifyContent: "space-between",
        transition: "all 0.2s",
        height: "36px", // Match NumberInput height
    };

    const portionNameStyle: React.CSSProperties = {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        flex: 1,
    };

    const dropdownContainerStyle: React.CSSProperties = {
        position: "absolute",
        top: "100%",
        left: "0",
        right: "0",
        backgroundColor: "#ffffff",
        border: "1px solid #cbd5e0",
        borderRadius: "6px",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        zIndex: 1000,
        maxHeight: "200px",
        overflowY: "auto",
    };

    const dropdownItemStyle: React.CSSProperties = {
        padding: "8px 12px",
        cursor: "pointer",
        borderBottom: "1px solid #e2e8f0",
        fontSize: "12px",
        transition: "background-color 0.2s",
    };

    const actionsContainerStyle: React.CSSProperties = {
        display: "flex",
        gap: "4px",
        justifyContent: "flex-end",
    };

    const iconButtonStyle: React.CSSProperties = {
        width: "32px",
        height: "32px",
        border: "none",
        borderRadius: "4px",
        backgroundColor: "#f7fafc",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "14px",
        transition: "background-color 0.2s",
    };

    const searchButtonStyle: React.CSSProperties = {
        ...iconButtonStyle,
        color: "#3182ce",
    };

    const detailButtonStyle: React.CSSProperties = {
        ...iconButtonStyle,
        color: "#805ad5",
    };

    const deleteButtonStyle: React.CSSProperties = {
        ...iconButtonStyle,
        color: "#e53e3e",
    };

    const [isNameTruncated, setIsNameTruncated] = useState(false);
    const nameRef = useRef<HTMLDivElement>(null);
    
    // State for nutrients hover
    const [showNutrientsHover, setShowNutrientsHover] = useState(false);
    const [nutrientsHoverPosition, setNutrientsHoverPosition] = useState({ x: 0, y: 0 });
    const [hoverNutrients, setHoverNutrients] = useState<{ name: string; amount: number; unit: string }[]>([]);
    const [loadingNutrients, setLoadingNutrients] = useState(false);
    const nutrientsHoverRef = useRef<HTMLDivElement>(null);
    const nutrientsButtonRef = useRef<HTMLButtonElement>(null);

    // Check if name is truncated
    useEffect(() => {
        const checkTruncation = () => {
            if (nameRef.current) {
                setIsNameTruncated(
                    nameRef.current.scrollWidth > nameRef.current.clientWidth
                );
            }
        };
        checkTruncation();
        window.addEventListener('resize', checkTruncation);
        return () => window.removeEventListener('resize', checkTruncation);
    }, [currentResult.displayName]);

    // Fetch nutrients when hover is shown
    useEffect(() => {
        if (!showNutrientsHover || !searchResult.refCode || !client || !showDetailedNutrients) return;

        const fetchNutrients = async () => {
            setLoadingNutrients(true);
            try {
                // Check if we already have detailed food data
                let foodData = detailedFoodData;
                
                if (!foodData) {
                    // Fetch if we don't have it
                    const response = await client.getFoodByRefCode(searchResult.refCode);
                    foodData = response;
                    setDetailedFoodData(response);
                }

                if (foodData?.results?.[0]?.ingredients?.[0]) {
                    const ingredient = foodData.results[0].ingredients[0];
                    
                    if (ingredient.nutrients && selectedPortion) {
                        const effectiveQuantity = quantity;
                        const totalWeight = effectiveQuantity * selectedPortion.weight.value;
                        const scaleFactor = totalWeight / 100;
                        
                        // Get all nutrients and filter out zeros
                        const nutrients = ingredient.nutrients
                            .filter((n: any) => n.nutrient?.name && n.amount !== undefined && n.amount * scaleFactor > 0)
                            .map((n: any) => ({
                                name: n.nutrient.name,
                                amount: n.amount * scaleFactor,
                                unit: n.nutrient.unit || "",
                            }))
                            .sort((a: any, b: any) => {
                                const aStartsWithNumber = /^\d/.test(a.name);
                                const bStartsWithNumber = /^\d/.test(b.name);
                                
                                if (aStartsWithNumber && !bStartsWithNumber) return 1;
                                if (!aStartsWithNumber && bStartsWithNumber) return -1;
                                
                                return a.name.localeCompare(b.name);
                            });

                        setHoverNutrients(nutrients);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch nutrients:", error);
            } finally {
                setLoadingNutrients(false);
            }
        };

        fetchNutrients();
    }, [showNutrientsHover, searchResult.refCode, client, showDetailedNutrients, detailedFoodData, selectedPortion, quantity]);

    const handleNutrientsMouseEnter = useCallback(() => {
        if (!showDetailedNutrients || !searchResult.refCode) return;
        
        if (nutrientsButtonRef.current) {
            const rect = nutrientsButtonRef.current.getBoundingClientRect();
            setNutrientsHoverPosition({ 
                x: rect.left,
                y: rect.bottom + 5
            });
        }
        setShowNutrientsHover(true);
    }, [showDetailedNutrients, searchResult.refCode]);

    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    
    const handleNutrientsMouseLeave = useCallback((e: React.MouseEvent) => {
        // Check if we're moving to the hover panel itself
        const relatedTarget = e.relatedTarget as HTMLElement;
        if (relatedTarget && nutrientsHoverRef.current?.contains(relatedTarget)) {
            return;
        }
        
        // Add a delay before hiding to make it easier to reach the panel
        hoverTimeoutRef.current = setTimeout(() => {
            setShowNutrientsHover(false);
        }, 300); // 300ms delay
    }, []);
    
    // Clear timeout when entering again
    useEffect(() => {
        if (showNutrientsHover && hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
    }, [showNutrientsHover]);

    // Expose getResultWithStateQuantity to the parent component
    useImperativeHandle(ref, () => ({
        getResultWithStateQuantity,
    }));

    return (
        <>
            <style>
                {`
                    .search-result-row {
                        display: grid;
                        grid-template-columns: minmax(180px, 0.8fr) 240px 280px 100px;
                        align-items: center;
                        padding: 10px 12px;
                        border: 1px solid #e2e8f0;
                        border-radius: 8px;
                        background-color: #ffffff;
                        margin-bottom: 8px;
                        gap: 12px;
                        position: relative;
                    }
                    
                    /* Large desktop (1440px+) */
                    @media (min-width: 1440px) {
                        .search-result-row {
                            grid-template-columns: minmax(200px, 0.9fr) 260px 300px 110px;
                            gap: 16px;
                            padding: 12px 16px;
                        }
                    }
                    
                    /* Medium desktop (1024px - 1439px) */
                    @media (min-width: 1024px) and (max-width: 1439px) {
                        .search-result-row {
                            grid-template-columns: minmax(160px, 0.8fr) 240px 280px 100px;
                            gap: 12px;
                        }
                    }
                    
                    /* Small desktop (768px - 1023px) */
                    @media (min-width: 768px) and (max-width: 1023px) {
                        .search-result-row {
                            grid-template-columns: minmax(140px, 0.7fr) 220px 260px 90px;
                            gap: 8px;
                            padding: 8px 10px;
                        }
                    }
                `}
            </style>
            <div className="search-result-row">
                {/* Name and Brand */}
                <div style={nameContainerStyle}>
                    <div 
                        ref={nameRef}
                        style={nameStyle}
                        title={isNameTruncated ? currentResult.displayName : undefined}
                    >
                        {currentResult.displayName}
                    </div>
                    {currentResult.brandName && (
                        <div style={brandStyle} title={currentResult.brandName}>
                            {currentResult.brandName}
                        </div>
                    )}
                </div>

                {/* Macros */}
                <div style={{ position: "relative", display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={macrosContainerStyle}>
                        <div style={macroItemStyle}>
                            <div style={macroLabelStyle}>Calories</div>
                            <div style={macroValueStyle}>
                                {formatNutritionValue(
                                    currentResult.nutritionPreview?.calories,
                                    ""
                                )}
                            </div>
                        </div>
                        <div style={macroItemStyle}>
                            <div style={macroLabelStyle}>Protein</div>
                            <div style={macroValueStyle}>
                                {formatNutritionValue(
                                    currentResult.nutritionPreview?.protein
                                )}
                            </div>
                        </div>
                        <div style={macroItemStyle}>
                            <div style={macroLabelStyle}>Carbs</div>
                            <div style={macroValueStyle}>
                                {formatNutritionValue(
                                    currentResult.nutritionPreview?.carbs
                                )}
                            </div>
                        </div>
                        <div style={macroItemStyle}>
                            <div style={macroLabelStyle}>Fat</div>
                            <div style={macroValueStyle}>
                                {formatNutritionValue(
                                    currentResult.nutritionPreview?.fat
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Nutrients hover button */}
                    {showDetailedNutrients && searchResult.refCode && (
                        <button
                            ref={nutrientsButtonRef}
                            onMouseEnter={handleNutrientsMouseEnter}
                            onMouseLeave={handleNutrientsMouseLeave}
                            style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: "4px",
                                borderRadius: "4px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "background-color 0.2s",
                                fontSize: "16px",
                                color: "#6b7280",
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = "#f3f4f6";
                                e.currentTarget.style.color = "#3b82f6";
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                                e.currentTarget.style.color = "#6b7280";
                            }}
                            title="View all nutrients"
                        >
                            📊
                        </button>
                    )}
                </div>

                {/* Quantity */}
                <div style={quantityContainerStyle}>
                    <NumberInput
                        min={0}
                        value={quantity}
                        onChange={handleQuantityChange}
                    />
                    <span
                        style={unitStyle}
                        onClick={
                            searchResult.refCode
                                ? handlePortionClick
                                : undefined
                        }
                        title={
                            searchResult.refCode
                                ? `${
                                      selectedPortion?.name || portionInfo.unit
                                  } - Click to see available portions`
                                : selectedPortion?.name || portionInfo.unit
                        }
                        onMouseEnter={(e) => {
                            if (searchResult.refCode) {
                                e.currentTarget.style.backgroundColor =
                                    "#edf2f7";
                                e.currentTarget.style.borderColor = "#3182ce";
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (searchResult.refCode) {
                                e.currentTarget.style.backgroundColor =
                                    "#f7fafc";
                                e.currentTarget.style.borderColor = "#cbd5e0";
                            }
                        }}
                    >
                        <span style={portionNameStyle}>
                            {loadingPortions
                                ? "Loading..."
                                : selectedPortion?.name || portionInfo.unit}
                        </span>
                        {searchResult.refCode && (
                            <span
                                style={{
                                    fontSize: "10px",
                                    color: "#718096",
                                    flexShrink: 0,
                                }}
                            >
                                {loadingPortions ? "⟳" : "▼"}
                            </span>
                        )}
                    </span>
                    
                    {/* Total weight display */}
                    {selectedPortion && (
                        <span
                            style={{
                                fontSize: "11px",
                                color: "#718096",
                                fontStyle: "italic",
                                whiteSpace: "nowrap",
                                marginLeft: "-4px", // Reduce gap
                            }}
                        >
                            ({getTotalWeightInGrams(selectedPortion, quantity).displayText})
                        </span>
                    )}

                    {/* Portion Dropdown */}
                    {showPortionDropdown && availablePortions.length > 0 && (
                        <div ref={dropdownRef} style={dropdownContainerStyle}>
                            {availablePortions.map((portion, index) => (
                                <div
                                    key={index}
                                    style={dropdownItemStyle}
                                    onClick={() => handlePortionSelect(portion)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                            "#f7fafc";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                            "#ffffff";
                                    }}
                                >
                                    <div style={{ fontWeight: "600" }}>
                                        {portion.name}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: "10px",
                                            color: "#718096",
                                        }}
                                    >
                                        {getTotalWeightInGrams(portion, getEffectiveQuantity(portion)).displayText}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div style={actionsContainerStyle}>
                    <button
                        onClick={() => setShowSearchReplace(true)}
                        style={searchButtonStyle}
                        title="Search and replace ingredient"
                        onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "#e6f3ff")
                        }
                        onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor = "#f7fafc")
                        }
                    >
                        🔍
                    </button>
                    {searchResult.refCode && (
                        <button
                            onClick={handleDetailClick}
                            style={detailButtonStyle}
                            title="View detailed nutrition information"
                            onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                    "#f1ebff")
                            }
                            onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                    "#f7fafc")
                            }
                        >
                            ℹ️
                        </button>
                    )}
                    {onDelete && (
                        <button
                            onClick={onDelete}
                            style={deleteButtonStyle}
                            title="Delete ingredient"
                            onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                    "#fed7d7")
                            }
                            onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                    "#f7fafc")
                            }
                        >
                            🗑️
                        </button>
                    )}
                </div>
            </div>

            {/* Search and Replace Modal */}
            {showSearchReplace && (
                <IngredientSearchReplace
                    isOpen={showSearchReplace}
                    currentItem={getResultWithStateQuantity()}
                    onSelect={handleReplace}
                    onClose={() => setShowSearchReplace(false)}
                />
            )}

            {/* Food Detail Modal */}
            {showDetailModal && (
                <FoodDetailModal
                    food={
                        detailFoodData
                            ? transformFoodDetailData(detailFoodData)
                            : null
                    }
                    isOpen={showDetailModal}
                    onClose={() => setShowDetailModal(false)}
                    loading={loadingDetails}
                />
            )}
            
            {/* Nutrients Hover Panel */}
            {showNutrientsHover && showDetailedNutrients && (
                <div
                    ref={nutrientsHoverRef}
                    style={{
                        position: "fixed",
                        left: `${nutrientsHoverPosition.x}px`,
                        top: `${nutrientsHoverPosition.y}px`,
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                        zIndex: 1000,
                        minWidth: "280px",
                        maxWidth: "400px",
                        maxHeight: "400px",
                        display: "flex",
                        flexDirection: "column",
                    }}
                    onMouseEnter={() => {
                        if (hoverTimeoutRef.current) {
                            clearTimeout(hoverTimeoutRef.current);
                            hoverTimeoutRef.current = null;
                        }
                        setShowNutrientsHover(true);
                    }}
                    onMouseLeave={() => setShowNutrientsHover(false)}
                >
                    <div style={{
                        padding: "12px 16px",
                        borderBottom: "1px solid #e5e7eb",
                        backgroundColor: "#f9fafb",
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px",
                    }}>
                        <div style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#374151",
                        }}>
                            {currentResult.displayName}
                        </div>
                        <div style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginTop: "2px",
                        }}>
                            All Nutrients ({hoverNutrients.length} tracked)
                        </div>
                    </div>
                    
                    <div style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "8px 0",
                    }}>
                        {loadingNutrients ? (
                            <div style={{ 
                                padding: "20px", 
                                textAlign: "center",
                                color: "#6b7280",
                                fontSize: "14px",
                            }}>
                                Loading nutrients...
                            </div>
                        ) : hoverNutrients.length === 0 ? (
                            <div style={{ 
                                padding: "20px", 
                                textAlign: "center",
                                color: "#6b7280",
                                fontSize: "14px",
                            }}>
                                No detailed nutrient data available
                            </div>
                        ) : (
                            hoverNutrients.map((nutrient, index) => {
                                const formatName = (name: string) => {
                                    return name
                                        .split(/[_\s]+/)
                                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                        .join(" ");
                                };
                                
                                return (
                                    <div
                                        key={index}
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            padding: "8px 20px",
                                            borderBottom: index < hoverNutrients.length - 1 ? "1px solid #f3f4f6" : "none",
                                            transition: "background-color 0.2s",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = "#f9fafb";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = "transparent";
                                        }}
                                    >
                                        <span style={{ 
                                            color: "#374151", 
                                            fontSize: "13px",
                                            marginRight: "16px",
                                        }}>
                                            {formatName(nutrient.name)}
                                        </span>
                                        <div style={{ 
                                            display: "flex", 
                                            gap: "4px", 
                                            alignItems: "baseline",
                                            flexShrink: 0,
                                        }}>
                                            <span style={{ 
                                                color: "#1f2937", 
                                                fontSize: "13px",
                                                fontWeight: "600",
                                            }}>
                                                {Math.round(nutrient.amount * 100) / 100}
                                            </span>
                                            <span style={{ 
                                                color: "#6b7280", 
                                                fontSize: "11px",
                                            }}>
                                                {nutrient.unit}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </>
    );
});

SearchResultRow.displayName = 'SearchResultRow';